import {
    defineComponent,
    onMounted,
    ref,
} from "vue";
import { useLocale } from "../locales/i18n.js";

type CommunityWindowTrend = {
    previous: number;
    current: number;
    delta: number;
    percent: number;
};

type CommunityRecommendation = {
    level: "high" | "medium" | "low";
    title: string;
    description: string;
};

type CommunityAction = {
    urgency: "high" | "medium" | "low";
    icon: string;
    title: string;
    howTo: string;
    expectedSignal: string;
};

type CommunityInsights = {
    windows: {
        activityWindowDays: number;
        churnWindowDays: number;
        targetActiveInteractionsPerDay: number;
    };
    engagement: {
        messages: CommunityWindowTrend;
        voice: CommunityWindowTrend;
        total: CommunityWindowTrend;
        avgActiveInteractionsPerDay: number;
        targetPerDay: number;
    };
    membership: {
        joins: CommunityWindowTrend;
        leaves: CommunityWindowTrend;
        net: CommunityWindowTrend;
        churnRate: number;
    };
    coaching: {
        score: number;
        status: "warning" | "stable" | "strong";
        statusMessage: string;
        actions: CommunityAction[];
    };
    recommendations: CommunityRecommendation[];
};

function formatTrend(value: number) {
    return `${value > 0 ? "+" : ""}${Math.round(value * 10) / 10}%`;
}

function trendClass(trend: number) {
    if (trend > 10)
        return "positive";
    if (trend < -10)
        return "negative";
    return "neutral";
}

function coachingStatusClass(status: CommunityInsights["coaching"]["status"]) {
    return status === "strong" ? "strong" : status === "stable" ? "stable" : "warning";
}

export default defineComponent({
    setup() {
        const communityInsights = ref<CommunityInsights | null>(null);
        const { i18n } = useLocale();

        onMounted(async () => {
            const response = await fetch("/api/community-insights");
            if (!response.ok)
                return;

            const payload = await response.json();
            if (payload && payload.code)
                return;

            communityInsights.value = payload as CommunityInsights;
        });

        return () => (
            <>
                {communityInsights.value && (
                    <div class="home-insights">
                        <div class="home-section-header">
                            <div>
                                <span class="home-eyebrow">{i18n("tools.community_engagement.eyebrow")}</span>
                                <h2>{i18n("tools.community_engagement.title")}</h2>
                            </div>

                            <div>
                                {i18n(
                                    "tools.community_engagement.windows_description",
                                    communityInsights.value.windows.activityWindowDays,
                                    communityInsights.value.windows.churnWindowDays
                                )}
                            </div>
                        </div>

                        <div class="home-insights-grid">
                            <article class="home-insight-card">
                                <span class="home-insight-title">{i18n("tools.community_engagement.card_totals")}</span>
                                <strong>{communityInsights.value.engagement.total.current}</strong>
                                <small>{i18n("tools.community_engagement.card_totals_subtitle")}</small>

                                <div class={`home-insight-trend ${trendClass(communityInsights.value.engagement.total.percent)}`}>
                                    <i class="material-symbols-outlined">
                                        {communityInsights.value.engagement.total.percent > 0 ? "trending_up" : communityInsights.value.engagement.total.percent < 0 ? "trending_down" : "trending_flat"}
                                    </i>
                                    {formatTrend(communityInsights.value.engagement.total.percent)}
                                </div>

                                <div class="home-insight-details">
                                    <span>{i18n("tools.community_engagement.metrics.messages")}: {communityInsights.value.engagement.messages.current}</span>
                                    <span>{i18n("tools.community_engagement.metrics.voice")}: {communityInsights.value.engagement.voice.current}</span>
                                    <span>{i18n("tools.community_engagement.metrics.target_per_day")}: {communityInsights.value.engagement.targetPerDay}{i18n("tools.community_engagement.per_day_unit")}</span>
                                    <span>{i18n("tools.community_engagement.metrics.current_per_day")}: {communityInsights.value.engagement.avgActiveInteractionsPerDay}{i18n("tools.community_engagement.per_day_unit")}</span>
                                </div>
                            </article>

                            <article class="home-insight-card">
                                <span class="home-insight-title">{i18n("tools.community_engagement.card_members")}</span>
                                <strong>{communityInsights.value.membership.net.current}</strong>
                                <small>{i18n("tools.community_engagement.card_members_subtitle")}</small>

                                <div class={`home-insight-trend ${trendClass(communityInsights.value.membership.net.percent)}`}>
                                    <i class="material-symbols-outlined">
                                        {communityInsights.value.membership.net.percent > 0 ? "trending_up" : communityInsights.value.membership.net.percent < 0 ? "trending_down" : "trending_flat"}
                                    </i>
                                    {formatTrend(communityInsights.value.membership.net.percent)}
                                </div>

                                <div class="home-insight-details">
                                    <span>{i18n("tools.community_engagement.metrics.joins")}: {communityInsights.value.membership.joins.current}</span>
                                    <span>{i18n("tools.community_engagement.metrics.leaves")}: {communityInsights.value.membership.leaves.current}</span>
                                    <span>{i18n("tools.community_engagement.metrics.churn")}: {communityInsights.value.membership.churnRate}%</span>
                                </div>
                            </article>

                            <article class="home-insight-card">
                                <span class="home-insight-title">{i18n("tools.community_engagement.card_recommendations")}</span>

                                <ul class="home-insight-recommendations">
                                    {communityInsights.value.coaching.actions.map((action) => (
                                        <li class={`home-insight-action ${action.urgency}`}>
                                            <i class="material-symbols-outlined">{action.icon}</i>
                                            <div class="home-insight-action-copy">
                                                <strong>{action.title}</strong>
                                                <span>{action.howTo}</span>
                                                <small>{action.expectedSignal}</small>
                                            </div>
                                        </li>
                                    ))}

                                    {!communityInsights.value.coaching.actions.length && (
                                        <li class="home-insight-recommendation low">
                                            <strong>{i18n("tools.community_engagement.recommendations_empty_title")}</strong>
                                            <span>{i18n("tools.community_engagement.recommendations_empty_description")}</span>
                                        </li>
                                    )}
                                </ul>
                            </article>

                            <article class="home-insight-card">
                                <span class="home-insight-title">{i18n("tools.community_engagement.card_health")}</span>
                                <strong>{communityInsights.value.coaching.score}/100</strong>
                                <small>{communityInsights.value.coaching.statusMessage}</small>

                                <div class={`home-insight-status ${coachingStatusClass(communityInsights.value.coaching.status)}`}>
                                    {communityInsights.value.coaching.status === "strong"
                                        ? i18n("tools.community_engagement.health.strong")
                                        : communityInsights.value.coaching.status === "stable"
                                            ? i18n("tools.community_engagement.health.stable")
                                            : i18n("tools.community_engagement.health.warning")}
                                </div>

                                <ul class="home-insight-recommendations">
                                    {communityInsights.value.recommendations.map((recommendation) => (
                                        <li class={`home-insight-recommendation ${recommendation.level}`}>
                                            <strong>{recommendation.title}</strong>
                                            <span>{recommendation.description}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        </div>
                    </div>
                )}
            </>
        );
    },
});
